import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ButtonDirective } from '@coreui/angular-pro';
import { TranslatePipe } from 'i18n';

import { GuardianTokenInfo, HealthCompanionService } from '../../../services/health-companion.service';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

/**
 * Public page of a guardian consent link: /:lang/companion/guardian/:token
 * (plan §13.1). The parent reads what the consent is about, confirms or
 * declines; a given consent can be withdrawn through the same link. No login,
 * no health content.
 */
@Component({
  selector: 'app-companion-guardian-page',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ButtonDirective, FunnelShellComponent],
  templateUrl: './companion-guardian-page.component.html',
  styleUrls: ['./companion-guardian-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanionGuardianPageComponent {
  private route      = inject(ActivatedRoute);
  private companion  = inject(HealthCompanionService);
  private destroyRef = inject(DestroyRef);

  token   = signal('');
  loading = signal(true);
  info    = signal<GuardianTokenInfo | null>(null);
  busy    = signal(false);
  result  = signal<string | null>(null);

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(p => {
      this.token.set(p.get('token') ?? '');
      this.load();
    });
  }

  load(): void {
    if (!this.token()) { this.info.set({ status: 'invalid' }); this.loading.set(false); return; }
    this.companion.guardianInfo(this.token()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: i => { this.info.set(i); this.loading.set(false); },
      error: () => { this.info.set({ status: 'invalid' }); this.loading.set(false); }
    });
  }

  act(action: 'confirm' | 'decline' | 'revoke'): void {
    if (this.busy()) return;
    this.busy.set(true);
    this.companion.guardianAct(this.token(), action).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: r => { this.result.set(r.status); this.busy.set(false); this.load(); },
      error: () => { this.result.set('error'); this.busy.set(false); }
    });
  }
}
